import {
    AccumulativeShadows as DreiAccumulativeShadows,
} from '@react-three/drei';
import {
    forwardRef,
    memo,
    useImperativeHandle,
    useRef,
} from 'react';
import type { ElementRef } from 'react';
import {
    PROGRESSIVE_SHADOW_BLEND,
    PROGRESSIVE_SHADOW_MAP_SIZE,
    SHADOW_ALPHA_TEST,
    SHADOW_BLEND,
    SHADOW_OPACITY,
    SHADOW_RESOLUTION,
} from './config';
import DeterministicRandomizedLight from './DeterministicRandomizedLight';
import type {
    DeterministicRandomizedLightHandle,
} from './DeterministicRandomizedLight';
import type { ShadowModel } from './types';

type DreiShadowBakerHandle = ElementRef<typeof DreiAccumulativeShadows>;

export type ShadowBakerHandle = Pick<
    DreiShadowBakerHandle,
    'getMesh'|'reset'|'update'
>;

interface Props {
    lightRotationY?: number
    model?: ShadowModel
    progressive: boolean
    visible: boolean
}

const ShadowBaker = forwardRef<ShadowBakerHandle, Props>(function ShadowBaker({
    lightRotationY = 0,
    model,
    progressive,
    visible,
}, ref) {
    const modelRadius = model?.radius ?? 1;
    const modelHeight = model?.height ?? 0;
    const baker = useRef<DreiShadowBakerHandle>(null);
    const randomizedLight = useRef<DeterministicRandomizedLightHandle>(null);

    useImperativeHandle(ref, () => ({
        getMesh: () => {
            if(!baker.current) {
                throw new Error('Shadow baker was not initialized');
            }
            return baker.current.getMesh();
        },
        reset: () => {
            randomizedLight.current?.reset();
            baker.current?.reset();
        },
        update: frames => {
            baker.current?.update(frames);
        },
    }), []);

    return <DreiAccumulativeShadows
        ref={baker}
        alphaTest={SHADOW_ALPHA_TEST}
        blend={progressive ? PROGRESSIVE_SHADOW_BLEND : SHADOW_BLEND}
        color="white"
        frames={Infinity}
        limit={0}
        opacity={SHADOW_OPACITY}
        position={[0, -modelHeight / 2, 0]}
        resolution={SHADOW_RESOLUTION}
        scale={modelRadius * 4}
        temporal={false}
        visible={visible}
    >
        {/* The +X/+Z placement is the camera-local key-light direction. The
        requested preview view rotates every deterministic sample. */}
        <DeterministicRandomizedLight
            ref={randomizedLight}
            ambient={0.5}
            amount={8}
            bias={0.0001}
            intensity={1}
            mapSize={progressive ? PROGRESSIVE_SHADOW_MAP_SIZE : SHADOW_RESOLUTION}
            position={[modelRadius, modelRadius * 2, modelRadius]}
            radius={modelRadius}
            rotationY={lightRotationY}
            size={modelRadius * 4}
        />
    </DreiAccumulativeShadows>;
});

export default memo(ShadowBaker);
